import React, { useContext, useRef, useState } from 'react';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { CollectionInvoicePdf } from '../model';
import { filenameContainsInvoiceNumber } from '../utils/invoiceFilename';

interface UseInvoicePdfUploadArgs {
  location: string;
  acctNo: string;
  onUploaded?: (applyTo: string, pdf: CollectionInvoicePdf) => void;
}

export function useInvoicePdfUpload({ location, acctNo, onUploaded }: UseInvoicePdfUploadArgs) {
  const { showErrorSnackBar } = useContext(SnackBarContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingApplyToRef = useRef<string | null>(null);
  const [uploadingApplyTo, setUploadingApplyTo] = useState<string | null>(null);
  const canUpload = Boolean(location && acctNo);

  const triggerUpload = (applyTo: string) => {
    pendingApplyToRef.current = applyTo;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const applyTo = pendingApplyToRef.current;
    e.target.value = '';
    pendingApplyToRef.current = null;
    if (!file || !applyTo || !canUpload) return;

    if (!filenameContainsInvoiceNumber(file.name, applyTo)) {
      window.alert(`Filename must include the invoice number (${applyTo}). Upload cancelled.`);
      return;
    }

    setUploadingApplyTo(applyTo);
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('location', location);
    formData.append('acct_no', acctNo);
    formData.append('apply_to', applyTo);

    try {
      const res = await axios.post('/collections/edoc_invoice_imports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const payload = res.data as { pdf?: CollectionInvoicePdf } | undefined;
      if (!payload?.pdf) {
        showErrorSnackBar?.('Failed to upload PDF');
        return;
      }
      onUploaded?.(applyTo, payload.pdf);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to upload PDF';
      showErrorSnackBar?.(msg);
    } finally {
      setUploadingApplyTo(null);
    }
  };

  return { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo, canUpload };
}
